import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Package, ShieldCheck, ArrowRight } from "lucide-react";
import { api, money, resolveImg, track } from "../lib/api";
import { AddKitButton } from "../components/AddKitButton";
import { ProductCard } from "../components/ProductCard";
import { useCart } from "../context/CartContext";

const LEVELS = [
  { key: "basic", label: "Basic", tag: "Essentials" },
  { key: "complete", label: "Complete", tag: "Most Popular" },
  { key: "advanced", label: "Advanced", tag: "Maximum Readiness" },
];
const ROLE_LABEL = { core: "Core", support: "Support", enhancement: "Enhancement" };

export default function KitDetail() {
  const { slug } = useParams();
  const [kit, setKit] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [selected, setSelected] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    setKit(null);
    setSelected([]);
    api.get(`/kits/${slug}`).then(({ data }) => {
      setKit(data);
      track("kit_view", "kit_detail", { kit: data.slug, level: data.level });
    }).catch(() => {});
    api.get("/kits").then(({ data }) => setSiblings(data)).catch(() => {});
  }, [slug]);

  if (!kit) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-black/40 text-sm">Loading kit…</div>;

  const family = siblings.filter((k) => k.family === kit.family);
  const addons = kit.addon_products || [];
  const selectedAddons = addons.filter((a) => selected.includes(a.slug));
  const addonTotal = selectedAddons.reduce((s, a) => s + (a.sale_price || a.price), 0);
  const total = kit.bundle_price + addonTotal;

  const toggleAddon = (p) => {
    setSelected((prev) => {
      const on = !prev.includes(p.slug);
      track("kit_addon_toggle", "kit_detail", { kit: kit.slug, addon: p.slug, selected: on });
      return on ? [...prev, p.slug] : prev.filter((s) => s !== p.slug);
    });
  };

  const addSelectedAddons = () => {
    selectedAddons.forEach((p) => {
      let price = p.sale_price || p.price, sku_code = null, variant_label = null;
      if (p.variations?.skus?.length) {
        const sku = p.variations.skus.find((s) => s.inventory > 0) || p.variations.skus[0];
        sku_code = sku.sku_code;
        price = sku.price;
        variant_label = Object.values(sku.specs || {}).join(" / ");
      }
      addItem({ item_type: "product", slug: p.slug, name: p.name, price, image: p.images?.[0], sku_code, variant_label });
    });
    if (selectedAddons.length) track("kit_addon_add", "kit_detail", { kit: kit.slug, addons: selectedAddons.map((a) => a.slug) });
    setSelected([]);
  };

  const ctaLabel = `Add ${kit.level === "complete" ? "Complete" : kit.level === "basic" ? "Basic" : "Advanced"} Kit to Cart${selectedAddons.length ? ` — ${money(total)}` : ""}`;

  return (
    <div className="pb-24 lg:pb-0">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <Link to="/kits" className="text-xs font-bold uppercase tracking-widest text-black/50 hover:text-[#4A5D23]">← All Ready Kits</Link>

        {/* HEADER */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-6">
          <div className="aspect-[4/3] overflow-hidden bg-[#F4F3F0] border border-black/10">
            <img src={resolveImg(kit.image)} alt={kit.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#4A5D23] font-mono">{kit.family_name}</p>
            <h1 className="font-heading text-3xl md:text-4xl uppercase font-black tracking-tighter mt-2">{kit.name}</h1>
            <p className="text-black/60 mt-3 text-sm md:text-base">{kit.purpose || kit.description}</p>

            {/* LEVEL SELECTOR */}
            {family.length > 1 && (
              <div className="mt-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 mb-2">How prepared do you want to be?</p>
                <div data-testid="level-selector" className="grid grid-cols-3 gap-px bg-black/10 border border-black/10">
                  {LEVELS.map((lv) => {
                    const k = family.find((x) => x.level === lv.key);
                    if (!k) return <div key={lv.key} className="bg-[#F4F3F0]" />;
                    const active = k.slug === kit.slug;
                    return (
                      <Link key={lv.key} data-testid={`level-btn-${lv.key}`} to={`/kit/${k.slug}`}
                        onClick={() => track("kit_level_select", "kit_detail", { family: kit.family, level: lv.key })}
                        className={`p-3 text-center ${active ? "bg-[#4A5D23] text-white" : "bg-white hover:bg-[#F4F3F0]"}`}>
                        <span className="block text-xs font-bold uppercase tracking-widest">{lv.label}</span>
                        <span className={`block text-[9px] uppercase tracking-wider mt-0.5 ${active ? "text-white/70" : "text-black/40"}`}>{lv.tag}</span>
                        <span className={`block text-sm font-bold mt-1 ${active ? "" : "text-black/70"}`}>{money(k.bundle_price)}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* PRICE */}
            <div className="mt-6 border border-black/10 bg-[#F4F3F0] p-5">
              <div className="flex items-baseline gap-3 flex-wrap">
                <span data-testid="kit-total-price" className="font-heading font-black text-3xl">{money(total)}</span>
                <span className="text-sm text-black/40 line-through">{money(kit.retail_value + addonTotal)} buying individually</span>
                <span className="text-xs font-bold uppercase tracking-widest bg-[#4A5D23] text-white px-2 py-1">Save {money(kit.savings)} ({kit.savings_pct}%)</span>
              </div>
              <div className="flex items-center gap-4 mt-3 text-[11px] text-black/50">
                <span className="flex items-center gap-1.5"><Package size={13} className="text-[#4A5D23]" /> {kit.item_count} products{selectedAddons.length ? ` + ${selectedAddons.length} add-on${selectedAddons.length > 1 ? "s" : ""}` : ""}</span>
                <span className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#4A5D23]" /> {kit.available ? "In stock — ships fast" : "Temporarily unavailable"}</span>
              </div>
              {kit.available ? (
                <AddKitButton kit={kit} label={ctaLabel} testId="kit-detail-add-btn" onAdded={addSelectedAddons}
                  className="mt-4 w-full bg-[#4A5D23] text-white py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150" />
              ) : (
                <button data-testid="kit-detail-unavailable" disabled className="mt-4 w-full bg-black/10 text-black/40 py-4 text-sm font-bold uppercase tracking-widest">Temporarily Unavailable</button>
              )}
            </div>
          </div>
        </div>

        {/* WHAT'S INCLUDED */}
        <section className="mt-14">
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight uppercase font-bold">What's Included</h2>
          <div data-testid="kit-included-list" className="mt-6 border border-black/10 divide-y divide-black/10">
            {(kit.products || []).map((p) => (
              <div key={p.slug} className="flex gap-4 p-4 items-start bg-white">
                <Link to={`/product/${p.slug}`} className="shrink-0">
                  <img src={resolveImg(p.images?.[0])} alt={p.name} loading="lazy" className="w-16 h-16 md:w-20 md:h-20 object-cover bg-[#F4F3F0] border border-black/5" />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <Link to={`/product/${p.slug}`} className="font-heading font-bold text-sm uppercase tracking-tight leading-snug hover:text-[#4A5D23]">{p.name}</Link>
                    <span className="font-bold text-sm shrink-0">{money(p.sale_price || p.price)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {p.kit_role && <span className="text-[9px] font-bold uppercase tracking-widest bg-[#4A5D23]/10 text-[#4A5D23] px-1.5 py-0.5">{ROLE_LABEL[p.kit_role] || p.kit_role}</span>}
                    <span className="text-[10px] text-black/40">Qty {p.kit_qty || 1}</span>
                  </div>
                  {p.kit_why && <p className="text-xs text-black/55 mt-1.5"><span className="font-semibold text-black/70">Why it's included:</span> {p.kit_why}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* WHY YOU'LL WANT IT */}
        {kit.benefits?.length > 0 && (
          <section className="mt-14 bg-[#121212] text-white p-8 md:p-10">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight uppercase font-bold">Why You'll Want It</h2>
            <ul data-testid="kit-benefits" className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {kit.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-white/85">
                  <Check size={16} className="text-[#8FA84E] shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* CUSTOMIZE */}
        {addons.length > 0 && (
          <section className="mt-14">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight uppercase font-bold">Customize Your Kit</h2>
            <p className="text-black/50 text-sm mt-1">Included items are locked in. Add optional upgrades — the total updates live.</p>
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="border border-black/10 divide-y divide-black/5">
                {(kit.products || []).map((p) => (
                  <div key={p.slug} className="flex items-center gap-3 px-4 py-2.5 bg-[#F4F3F0]/60">
                    <span className="w-4 h-4 flex items-center justify-center bg-[#4A5D23] text-white shrink-0"><Check size={11} /></span>
                    <img src={resolveImg(p.images?.[0])} alt={p.name} loading="lazy" className="w-8 h-8 object-cover bg-white border border-black/5 shrink-0" />
                    <span className="flex-1 min-w-0 truncate text-xs text-black/70">{p.name}</span>
                    <span className="text-[10px] text-black/40 uppercase tracking-wider shrink-0">Included</span>
                  </div>
                ))}
                {addons.map((p) => (
                  <label key={p.slug} data-testid={`addon-toggle-${p.slug}`} className="flex items-center gap-3 px-4 py-2.5 bg-white cursor-pointer hover:bg-[#F4F3F0] transition-colors duration-150">
                    <input type="checkbox" checked={selected.includes(p.slug)} onChange={() => toggleAddon(p)}
                      className="w-4 h-4 accent-[#4A5D23] shrink-0" />
                    <img src={resolveImg(p.images?.[0])} alt={p.name} loading="lazy" className="w-8 h-8 object-cover bg-[#F4F3F0] border border-black/5 shrink-0" />
                    <span className="flex-1 min-w-0 truncate text-xs">{p.name}</span>
                    <span className="text-xs font-bold text-[#4A5D23] shrink-0">+{money(p.sale_price || p.price)}</span>
                  </label>
                ))}
              </div>
              <div className="border border-black/10 bg-[#F4F3F0] p-6 h-fit lg:sticky lg:top-24">
                <h3 className="font-heading font-bold uppercase tracking-tight">Your Setup</h3>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-black/60">{kit.name}</span><span className="font-bold">{money(kit.bundle_price)}</span></div>
                  {selectedAddons.map((a) => (
                    <div key={a.slug} className="flex justify-between text-xs"><span className="text-black/50 truncate pr-2">+ {a.name}</span><span>{money(a.sale_price || a.price)}</span></div>
                  ))}
                  <div className="flex justify-between border-t border-black/10 pt-2 mt-2">
                    <span className="font-bold">Total ({kit.item_count + selectedAddons.length} items)</span>
                    <span data-testid="customize-total" className="font-heading font-black text-lg">{money(total)}</span>
                  </div>
                  <p className="text-[11px] text-[#4A5D23] font-bold">Kit savings: {money(kit.savings)}</p>
                </div>
                {kit.available && (
                  <AddKitButton kit={kit} label={ctaLabel} testId="customize-add-btn" onAdded={addSelectedAddons}
                    className="mt-4 w-full bg-[#121212] text-white py-3.5 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150" />
                )}
              </div>
            </div>
          </section>
        )}

        {/* COMPLETE YOUR SETUP */}
        {addons.length > 0 && (
          <section className="mt-14">
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl tracking-tight uppercase font-bold">Complete Your Setup</h2>
                <p className="text-black/50 text-sm mt-1">Gear that pairs well with this kit.</p>
              </div>
              <Link to={`/shop?good_for=${encodeURIComponent(kit.family === "edc" ? "Everyday Carry" : kit.family === "outdoor" ? "Outdoor" : kit.family === "vehicle" ? "Vehicle" : kit.family === "home" ? "Home Security" : kit.family === "personal-security" ? "Personal Security" : "Preparedness")}`}
                className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest hover:text-[#4A5D23]">More Gear <ArrowRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {addons.slice(0, 4).map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
          </section>
        )}
      </div>

      {/* STICKY MOBILE CTA */}
      {kit.available && (
        <div data-testid="sticky-mobile-cta" className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-black/10 p-3 flex items-center gap-3">
          <div className="min-w-0">
            <p className="font-heading font-black text-lg leading-none">{money(total)}</p>
            <p className="text-[10px] text-[#4A5D23] font-bold uppercase tracking-wider">Save {money(kit.savings)}</p>
          </div>
          <AddKitButton kit={kit} label="Add Kit to Cart" testId="sticky-add-btn" onAdded={addSelectedAddons}
            className="flex-1 bg-[#4A5D23] text-white py-3.5 text-xs font-bold uppercase tracking-widest" />
        </div>
      )}
    </div>
  );
}
